import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/seo/SEO";
import { buildBlogPostingLd } from "@/lib/jsonld";
import { ArrowLeft, Calendar } from "lucide-react";

function formatDate(iso, locale = "es-ES") {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(locale, {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function BlogPost() {
    const { t } = useTranslation();
    const dateLocale = t("blog.locale");
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/blog/${slug}`)
            .then((r) => setPost(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading)
        return (
            <article className="pt-32 pb-24 min-h-screen flex items-center justify-center">
                <p className="text-stone italic font-editorial">{t("blog.loading")}</p>
            </article>
        );

    if (!post)
        return (
            <article className="pt-32 pb-24 text-center max-w-2xl mx-auto px-6">
                <h1 className="font-display text-4xl text-ivory">{t("blog.notFound")}</h1>
                <Link to="/blog" className="btn-ghost mt-10 inline-flex" data-cursor="hover">
                    {t("blog.back")}
                </Link>
            </article>
        );

    return (
        <article className="pt-32 pb-24 paper-grain">
            <SEO
                title={post.titulo}
                description={post.extracto || post.contenido.slice(0, 160)}
                image={post.imagen_url}
                path={`/blog/${post.slug}`}
                type="article"
                publishedTime={post.fecha_pub || post.creado_en}
                author={post.autor}
                breadcrumbs={[
                    { name: t("nav.home"), item: "/" },
                    { name: t("nav.blog"), item: "/blog" },
                    { name: post.titulo, item: `/blog/${post.slug}` },
                ]}
                jsonLd={buildBlogPostingLd(post)}
            />

            <section className="max-w-3xl mx-auto px-6">
                <Link
                    to="/blog"
                    className="inline-flex items-center gap-2 text-stone hover:text-gold transition-colors text-xs uppercase tracking-[0.25em] font-body mb-12"
                    data-cursor="hover"
                >
                    <ArrowLeft size={14} /> {t("blog.backShort")}
                </Link>

                <motion.header
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1 }}
                    className="mb-12"
                >
                    <p className="font-editorial italic text-xs tracking-[0.25em] uppercase text-gold flex items-center gap-2">
                        <Calendar size={12} />
                        {formatDate(post.fecha_pub || post.creado_en, dateLocale)} · {post.autor}
                    </p>
                    <h1 className="font-display text-[clamp(2rem,6vw,4.5rem)] text-ivory font-light leading-[1.05] mt-6">
                        {post.titulo}
                    </h1>
                    {post.extracto && (
                        <p className="font-editorial italic text-stone text-lg mt-6 leading-relaxed">
                            {post.extracto}
                        </p>
                    )}
                </motion.header>

                {post.imagen_url && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 1 }}
                        className="aspect-[16/9] mb-14 border border-gold/20 overflow-hidden bg-bg-alt flex items-center justify-center"
                    >
                        <img
                            src={post.imagen_url}
                            alt={post.titulo}
                            className="max-w-full max-h-full w-auto h-auto object-contain"
                        />
                    </motion.div>
                )}

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 1 }}
                    className="font-body text-ivory text-lg leading-[1.85] space-y-6 drop-cap"
                    dangerouslySetInnerHTML={{ __html: post.contenido }}
                />

                <div className="border-t border-gold/15 mt-20 pt-10 text-center">
                    <Link to="/blog" className="btn-ghost" data-cursor="hover">
                        {t("blog.back")}
                    </Link>
                </div>
            </section>
        </article>
    );
}
